import React, { useMemo } from 'react';
import { useAppSelector } from 'redux/hooks/useReduxThunk';
import { useLanguage } from '@hydrogen/elements.core.i18n';
import { forgotPasswordAnswersSelector } from 'redux/forgotPassword/forgotPasswordSelectors';
import { ForgotPasswordType } from 'redux/forgotPassword/forgotPasswordSlice';
import { sendResetKeyForgotPassword } from 'services/contacts';
import {
    StepLayout,
    MultiStepButtons,
    mtanFieldName,
    Mtan,
    parseReplace,
} from '@dekafvv/sharedfrontend';
import { FormProvider } from 'react-hook-form';
import { useGetPreliminaryKeyForgotPassword } from 'shared/Api/model/contacts/queries';
import { useMtanForm } from 'features/mtan';
import { useMutation } from 'react-query';

export const ForgotPasswordMtan: React.FC<Props> = ({ onNext, onPrev }) => {
    const { t } = useLanguage();

    const { email } = useAppSelector<ForgotPasswordType>(
        forgotPasswordAnswersSelector,
    );

    const {
        generatedSessionId,
        setGeneratedSessionId,
        methods,
        setMtanError,
    } = useMtanForm(9);

    const { handleSubmit } = methods;

    const { mutate, isLoading } = useMutation({
        mutationFn: sendResetKeyForgotPassword,
        onSuccess: (response) => {
            if (response.data.Result === 'Success') {
                onNext();
            } else if (response.data.Result === 'MTanVerificationFailed') {
                setMtanError(t('error.mtan.incorrect-mtan'));
            }
        },
        onError: () => {
            setMtanError(t('error.mtan.incorrect-mtan'));
        },
    });

    const handleOnNext = handleSubmit(async (val) => {
        mutate({
            preliminaryKey: val[mtanFieldName],
            sessionId: generatedSessionId,
            redirectUrl: '',
        });
    });

    const onNextBtn = useMemo(() => ({
        title: t('button.confirm'),
        action: handleOnNext,
    }), [handleOnNext, t]);

    const onPrevBtn = useMemo(() => ({
        title: t('button.interrupt'),
        action: onPrev,
    }), [onPrev, t]);

    const translations = useMemo(() => ({
        subheader: 'mtan.subheader',
        text1: parseReplace(t('wizard.forgot-password.mTAN.text'), {
            $email$: email,
        }),
    }), [email, t]);

    const mtanQuery = useMemo(() => ({
        query: useGetPreliminaryKeyForgotPassword,
        body: {
            email,
            username: email,
        },
    }), [email]);

    return (
        <FormProvider {...methods}>
            <StepLayout>
                <Mtan
                    className="max-w-[520px]"
                    mtanQueryWBody={mtanQuery}
                    length={9}
                    isLoading={isLoading}
                    translations={translations}
                    setGeneratedSessionId={setGeneratedSessionId}
                    type="text"
                />
                <MultiStepButtons onNext={onNextBtn} onPrev={onPrevBtn} />
            </StepLayout>
        </FormProvider>
    );
};

type Props = {
    onNext: () => void;
    onPrev: () => void;
};
